import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaFileInvoice,
  FaUserTie,
  FaBuilding,
  FaCalendarAlt,
  FaPercentage,
  FaTag,
  FaChevronLeft,
  FaSave,
  FaInfoCircle,
  FaEye
} from "react-icons/fa";
import Toast from "../../../Components/Toast";
import useAuthStore from "../../../store/useAuthStore";
import useCompanyStore from "../../../store/useCompanyStore";
import PremiumInvoiceTable from "../../../Components/PremiumInvoiceTable";

export default function NewQuotation() {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const { companyInfo } = useCompanyStore();

  // Client State
  const [client, setClient] = useState({
    name: "",
    address: "",
    phone: "",
    email: ""
  });

  // Quotation Meta State
  const [quotationMeta, setQuotationMeta] = useState({
    number: "",
    date: new Date().toISOString().split('T')[0],
    expiryDate: "",
    status: "Pending"
  });

  // Financials State
  const [financials, setFinancials] = useState({
    items: [],
    subTotal: 0,
    taxRate: 0,
    taxAmount: 0,
    discount: 0,
    totalAmount: 0,
    notes: "",
    terms: ""
  });

  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, type: "", title: "", message: "" });

  const handleItemsChange = (items, totals) => {
    setFinancials(prev => ({
      ...prev,
      items,
      ...totals
    }));
  };

  const handleSave = async () => {
    const missingFields = [];
    if (!client.name.trim()) missingFields.push("Client Name");
    if (!quotationMeta.number.trim()) missingFields.push("Quotation Number");
    if (!quotationMeta.date) missingFields.push("Quotation Date");

    const validItems = financials.items.filter(
      (item) => item.item.trim() !== "" && item.qty > 0 && item.unitPrice > 0
    );
    if (validItems.length === 0) missingFields.push("At least one item with description, quantity, and price");

    if (missingFields.length > 0) {
      setToast({
        show: true,
        type: "error",
        title: "Required Fields Missing",
        message: `Please fill in: ${missingFields.join(", ")}`,
      });
      return;
    }

    setLoading(true);
    const cleanItems = financials.items.filter(
      (item) => item.item.trim() !== "" && item.qty > 0 && item.unitPrice > 0
    );
    const quotationData = {
      to: client.name,
      clientAddress: client.address,
      clientPhone: client.phone,
      clientEmail: client.email,
      title: quotationMeta.number,
      date: quotationMeta.date,
      expiryDate: quotationMeta.expiryDate,
      quotationItems: cleanItems,
      subTotal: financials.subTotal,
      taxRate: financials.taxRate,
      taxAmount: financials.taxAmount,
      discount: financials.discount,
      totalAmount: financials.totalAmount,
      notes: financials.notes,
      terms: financials.terms,
      companyDetails: {
        name: companyInfo?.name,
        address: companyInfo?.address,
        phone: companyInfo?.phone,
        email: companyInfo?.email,
        bankName: companyInfo?.bankName,
        accountNo: companyInfo?.accountNo,
        logo: companyInfo?.logo
      }
    };

    try {
      const response = await fetch("/api/addQuotation", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(quotationData),
      });

      if (response.ok) {
        setToast({ show: true, type: "success", title: "Success", message: "Quotation created successfully!" });
        setTimeout(() => navigate("/quotation"), 2000);
      } else {
        let errorMsg = "Failed to save quotation.";
        try {
          const data = await response.json();
          errorMsg = data.error || errorMsg;
        } catch (_) {}
        setToast({ show: true, type: "error", title: "Error", message: errorMsg });
      }
    } catch (err) {
      setToast({ show: true, type: "error", title: "Network Error", message: "Unable to reach the server. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = () => {
    const previewData = {
      to: client.name,
      clientAddress: client.address,
      clientPhone: client.phone,
      clientEmail: client.email,
      title: quotationMeta.number,
      date: quotationMeta.date,
      expiryDate: quotationMeta.expiryDate,
      items: financials.items,
      subTotal: financials.subTotal,
      taxRate: financials.taxRate,
      taxAmount: financials.taxAmount,
      discount: financials.discount,
      totalAmount: financials.totalAmount,
      notes: financials.notes,
      terms: financials.terms,
      companyDetails: {
        name: companyInfo?.name,
        address: companyInfo?.address,
        phone: companyInfo?.phone,
        email: companyInfo?.email,
        bankName: companyInfo?.bankName,
        accountNo: companyInfo?.accountNo,
        logo: companyInfo?.logo
      }
    };
    navigate("/QuotationPDF", { state: { PDFData: previewData } });
  };

  return (
    <div className="min-h-screen bg-slate-50/50 p-4 md:p-10 font-sans tracking-tight">
      <div className="max-w-[1400px] mx-auto space-y-8">

        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 px-2">
          <div className="space-y-1">
            <button
              onClick={() => navigate("/quotation")}
              className="flex items-center gap-2 text-slate-400 hover:text-indigo-600 font-semibold text-sm transition-all"
            >
              <FaChevronLeft className="text-[10px]" />
              Quotation List
            </button>
            <h1 className="text-4xl font-black text-slate-900 tracking-tighter">
              New <span className="text-indigo-600">Quotation</span>
            </h1>
            <p className="text-slate-500 font-medium">Create a professional estimate for your client.</p>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={handlePreview}
              className="flex items-center gap-3 px-8 py-4 bg-white border-2 border-slate-900 text-slate-900 rounded-[1.8rem] font-black hover:bg-slate-50 transition-all shadow-xl shadow-slate-100"
            >
              <FaEye />
              Professional Preview
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex items-center gap-3 px-10 py-4 bg-slate-900 text-white rounded-[1.8rem] font-black hover:bg-rose-600 transition-all shadow-2xl shadow-slate-200 disabled:bg-slate-200 disabled:text-slate-400"
            >
              {loading ? <div className="w-5 h-5 border-4 border-white/20 border-t-white rounded-full animate-spin" /> : <FaSave />}
              Finalize & Save
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Client Info Card */}
            <div className="bg-white rounded-[2.5rem] border border-slate-200/60 shadow-xl p-8 space-y-6">
              <h2 className="text-xl font-black text-slate-800 flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600">
                  <FaUserTie />
                </div>
                Client Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 font-bold uppercase tracking-widest text-[10px] text-slate-400">
                <div className="space-y-2">
                  <label>Client Name *</label>
                  <input
                    type="text"
                    placeholder="Company or Individual Name"
                    className="w-full bg-slate-50 border-none rounded-2xl p-4 text-sm font-black text-slate-700 focus:ring-2 focus:ring-indigo-500"
                    value={client.name}
                    onChange={(e) => setClient({ ...client, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <label>Client Email</label>
                  <input
                    type="email"
                    placeholder="estimates@client.com"
                    className="w-full bg-slate-50 border-none rounded-2xl p-4 text-sm font-black text-slate-700 focus:ring-2 focus:ring-indigo-500"
                    value={client.email}
                    onChange={(e) => setClient({ ...client, email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <label>Phone Number</label>
                  <input
                    type="text"
                    placeholder="+975 17XXXXXX"
                    className="w-full bg-slate-50 border-none rounded-2xl p-4 text-sm font-black text-slate-700 focus:ring-2 focus:ring-indigo-500"
                    value={client.phone}
                    onChange={(e) => setClient({ ...client, phone: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <label>Billing Address</label>
                  <input
                    type="text"
                    placeholder="Thimphu, Bhutan"
                    className="w-full bg-slate-50 border-none rounded-2xl p-4 text-sm font-black text-slate-700 focus:ring-2 focus:ring-indigo-500"
                    value={client.address}
                    onChange={(e) => setClient({ ...client, address: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Items Table */}
            <div className="bg-white rounded-[2.5rem] border border-slate-200/60 shadow-xl p-8 space-y-6">
              <h2 className="text-xl font-black text-slate-800 flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600">
                  <FaFileInvoice />
                </div>
                Quotation Items
              </h2>
              <PremiumInvoiceTable
                onItemsChange={handleItemsChange}
                taxRate={financials.taxRate}
                discount={financials.discount}
              />
            </div>
          </div>

          {/* Right Column: Meta & Sidebar */}
          <div className="space-y-8">
            {/* Quotation Meta Card */}
            <div className="bg-slate-900 rounded-[2.5rem] p-8 shadow-2xl text-white space-y-6">
              <h2 className="text-xl font-black flex items-center gap-3">
                Quotation Meta
              </h2>
              <div className="space-y-5 font-bold uppercase tracking-widest text-[10px] text-slate-400">
                <div className="space-y-2">
                  <label className="flex items-center gap-2"><FaCalendarAlt /> Quotation Date *</label>
                  <input
                    type="date"
                    className="w-full bg-white/10 border-none rounded-2xl p-4 text-sm font-black text-white focus:ring-2 focus:ring-indigo-500"
                    value={quotationMeta.date}
                    onChange={(e) => setQuotationMeta({ ...quotationMeta, date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <label className="flex items-center gap-2"><FaCalendarAlt className="text-rose-400" /> Expiry Date</label>
                  <input
                    type="date"
                    className="w-full bg-white/10 border-none rounded-2xl p-4 text-sm font-black text-white focus:ring-2 focus:ring-indigo-500"
                    value={quotationMeta.expiryDate}
                    onChange={(e) => setQuotationMeta({ ...quotationMeta, expiryDate: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <label className="flex items-center gap-2"><FaInfoCircle /> Quotation Number *</label>
                  <input
                    type="text"
                    placeholder="QTN-2024-001"
                    className="w-full bg-white/10 border-none rounded-2xl p-4 text-sm font-black text-white focus:ring-2 focus:ring-indigo-500"
                    value={quotationMeta.number}
                    onChange={(e) => setQuotationMeta({ ...quotationMeta, number: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Tax & Discount Card */}
            <div className="bg-white rounded-[2.5rem] p-8 border border-slate-200/60 shadow-xl space-y-6">
              <h2 className="text-xl font-black text-slate-800 flex items-center gap-3">
                Financial Adjustments
              </h2>
              <div className="grid grid-cols-1 gap-6 font-bold uppercase tracking-widest text-[10px] text-slate-400">
                <div className="space-y-2">
                  <label className="flex items-center gap-2"><FaPercentage className="text-indigo-500" /> Tax Rate (%)</label>
                  <input
                    type="number"
                    className="w-full bg-slate-50 border-none rounded-2xl p-4 text-sm font-black text-slate-700 focus:ring-2 focus:ring-indigo-500"
                    value={financials.taxRate}
                    onChange={(e) => setFinancials({ ...financials, taxRate: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <label className="flex items-center gap-2"><FaTag className="text-rose-500" /> Discount (Flat Amount)</label>
                  <input
                    type="number"
                    className="w-full bg-slate-50 border-none rounded-2xl p-4 text-sm font-black text-slate-700 focus:ring-2 focus:ring-indigo-500"
                    value={financials.discount}
                    onChange={(e) => setFinancials({ ...financials, discount: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
            </div>

            {/* Company Snapshot Preview */}
            <div className="bg-indigo-600 rounded-[2.5rem] p-8 text-white shadow-2xl space-y-4">
              <h4 className="text-[10px] font-black uppercase tracking-widest text-indigo-200 flex items-center gap-2">
                <FaBuilding /> Issuing Company
              </h4>
              <div className="space-y-1">
                <div className="text-lg font-black">{companyInfo?.name || "Your Company Name"}</div>
                <div className="text-xs font-medium text-indigo-100 opacity-80">{companyInfo?.address || "No address set"}</div>
                <div className="text-xs font-black mt-4 uppercase tracking-widest text-indigo-200 pt-4 border-t border-white/10">Banking Details</div>
                <div className="text-xs font-medium">{companyInfo?.bankName || "No bank set"}</div>
                <div className="text-sm font-black">{companyInfo?.accountNo || "-"}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {toast.show && (
        <Toast
          type={toast.type}
          title={toast.title}
          message={toast.message}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
}
